import HBMessage from "@/components/HBMessage";
import HBInfoDialog from "@/components/HBModal/info";
import {
  cancelBizOrder,
  getOrderToken,
  getPayMethod,
  getPublicTransferAccount,
  orderPay,
} from "@/services";
import useRequest from "@/utils/useReq";
import { useCountDown, useSafeState } from "ahooks";
import { Button } from "antd";
import { padStart } from "lodash";
import { useCallback, useMemo } from "react";
import styles from "./index.module.less";

/**
 * 对公转账
 * @param {{
 *  details?: Object[],
 *  coupons?: Object[],
 *  payParams?: Object,
 *  onCancelTransfer?: Function,
 * }} param0
 * @returns
 */
export default function PublicTransfer({
  details,
  coupons = [],
  payParams,
  onCancelTransfer,
}) {
  const discounts = details.filter((d) => d.type === "discount");
  const payList = details.filter((d) => d.skuId);

  const discountPrice = useMemo(() => {
    return discounts.reduce((pre, now) => pre + now.price, 0);
  }, [discounts]);

  const [account, setAccount] = useSafeState({});
  const [order, setOrder] = useSafeState({});

  useRequest(async () => {
    const { list: methods } = await getPayMethod();
    // 获取支付方式
    const payMethod = methods.find((item) => item.name === "线下支付-转账");
    // 生成订单token
    const { token } = await getOrderToken(payParams);
    // 根据token、支付方式生成订单
    const { err, order } = await orderPay({
      token: token,
      payment_method_id: payMethod.id,
    });

    if (err) {
      return;
    }

    setOrder(order);

    // 获取订单转账信息
    const accountInfo = await getPublicTransferAccount(order.order_no);
    setAccount(accountInfo);
  });

  const onCopy = useCallback(() => {
    copyAccount(account, order);
    HBMessage.success("复制成功");
  }, [account, order]);

  const onCancel = useCallback(() => {
    if (!order?.order_no) {
      // 订单未生成，直接取消
      onCancelTransfer && onCancelTransfer();
      return;
    }

    HBInfoDialog.open({
      title: "提示",
      content: (
        <div className={styles.confirm}>
          <div className={styles.title}>确定取消订单？</div>
          <div className={styles.desc}>
            若已转账，请勿取消订单。否则将影响会员开通。
          </div>
        </div>
      ),
      okText: "确认取消",
      cancelText: "我再想想",
      async onOk() {
        await cancelBizOrder(order);
        HBMessage.success("取消成功");
        onCancelTransfer && onCancelTransfer();
      },
    });
  }, [onCancelTransfer, order]);

  return (
    <div className={styles.transfer}>
      <div className={styles.account}>
        <div className={styles.title}>
          转账信息 <span>转账成功半小时内，系统自动为你开通会员</span>
        </div>

        <div className={styles.content}>
          <div>
            <span>转账金额：</span>
            <span>￥{order.amount}</span>
          </div>
          <div>
            <span>收款公司名称：</span>
            <span>{account.company}</span>
          </div>
          <div>
            <span>收款账户：</span>
            <span>{account.account}</span>
            <div className={styles.desc}>
              (每笔订单对应不同25位收款账号，请勿重复打款)
            </div>
          </div>
          <div>
            <span>开户行名称：</span>
            <span>{account.account_name}</span>
          </div>
        </div>
        <div className={styles.bottom}>
          <span className={styles.countdown}>
            请在{" "}
            <span>
              {" "}
              {order?.expiration_time && (
                <CountDown
                  targetTIme={
                    new Date(order?.created_at).valueOf() + 2592000000
                  }
                />
              )}
            </span>{" "}
            内完成转账，超时订单将自动取消
          </span>
          <div>
            <Button onClick={onCancel}>取消订单</Button>{" "}
            <Button type="primary" onClick={onCopy}>
              复制转账信息
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.orderDetail}>
        <div className={styles.title}>订单明细</div>
        {payList.map((item) => (
          <div className={styles.detailItem} key={item.name}>
            <div className={styles.name}>
              <span>{item.name}</span>
            </div>
            <div className={styles.desc}>
              {!item.years && <span>x{item.number}</span>}
              <span className={styles.time}>
                {(item.expireTime || item.years) && (
                  <>
                    {item.years && <span>{item.years}年 </span>} 到期时间{" "}
                    {item.expireTime}
                  </>
                )}
              </span>
            </div>

            <span className={styles.price}>
              ¥{item.activity?.price || item.price}
            </span>
          </div>
        ))}
        {discounts.map((discount) => (
          // 折抵信息
          <div className={styles.discount} key={discount.name}>
            <span>{discount.name}</span>
            <span className={styles.price}>-¥{-discount.price}</span>
          </div>
        ))}
        {coupons.map((coupon) => (
          <div className={styles.discount} key={coupon.id}>
            <span>优惠券</span>
            <span className={styles.price}>-¥{coupon.discount}</span>
          </div>
        ))}

        <div className={styles.payPrice}>
          <span>
            {discountPrice > 0 && (
              <>
                <span
                  className={styles.discount}
                >{`已抵扣¥${-discountPrice}`}</span>
                ，
              </>
            )}
            应付金额：
          </span>
          <span className="color-primary">
            ¥<span className={styles.num}>{order.amount || "--"}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function CountDown({ targetTIme }) {
  const [, formattedRes] = useCountDown({ targetDate: targetTIme });
  return (
    <span>
      {padStart(formattedRes.days, 2, "0")}天
      {padStart(formattedRes.hours, 2, "0")}:
      {padStart(formattedRes.minutes, 2, "0")}:
      {padStart(formattedRes.seconds, 2, "0")}
    </span>
  );
}

export function copyAccount(account, order) {
  navigator.clipboard.writeText(
    `转账金额：¥${order.amount}
收款公司名称：${account.company}
收款账户：${account.account}
开户行名称 ${account.account_name}`
  );
}
